import { useCallback } from 'react';
import { useDataProvider, useGetIdentity } from 'react-admin';

// Schema type for Endorsement Definitions data
export type EndorsementDefImportSchema = {
    endorsement: string;
    endorsement_alias: string[];
    endorsement_type: string;
    definition: string;
    tags: string[];
};

// Normalization function for Endorsement Definitions spreadsheet
// CSV columns: endorsement, endorsement_alias, endorsement_type, definition, tags
export function normalizeEndorsementDefs(
    data: Record<string, any>[]
): EndorsementDefImportSchema[] {
    console.log(`Normalizing ${data.length} endorsement definition records...`);
    const normalized: EndorsementDefImportSchema[] = [];
    const dataArray = Array.isArray(data) ? data : [data];
    if (dataArray.length === 0) return [];

    const parseToArray = (valueString: string): string[] => {
        if (!valueString) return [];
        return valueString
            .split(',')
            .map(item => item.trim())
            .filter(item => item !== '');
    };

    for (const row of dataArray) {
        // Try named columns — supports multiple header naming conventions
        const endorsement = String(
            row.endorsement ||
                row.Endorsement ||
                row['Endorsement (Title)'] ||
                ''
        ).trim();
        const aliasString = String(
            row.endorsement_alias || row['Endorsement Alias'] || row.alias || ''
        ).trim();
        const endorsementType = String(
            row.endorsement_type ||
                row['Endorsement Type'] ||
                row['Endorsement (Type)'] ||
                row.type ||
                ''
        ).trim();
        const definition = String(
            row.definition || row.Definition || row['Definition (Body)'] || ''
        ).trim();
        const tagsString = String(row.tags || row.Tags || '').trim();

        if (!endorsement || !endorsementType) continue;

        normalized.push({
            endorsement,
            endorsement_alias: parseToArray(aliasString),
            endorsement_type: endorsementType,
            definition,
            tags: parseToArray(tagsString),
        });
    }
    console.log(
        'Normalization complete for endorsement definitions.',
        normalized.slice(0, 5)
    );
    return normalized;
}

// Import hook for Endorsement Definitions
export function useEndorsementDefsImport(selectedSpreadsheet: string) {
    const { identity } = useGetIdentity();
    const dataProvider = useDataProvider();

    const processBatch = useCallback(
        async (batch: EndorsementDefImportSchema[]) => {
            const userId = identity?.id;
            if (userId === undefined || userId === null) {
                console.error(
                    'useEndorsementDefsImport: Missing user ID from identity.'
                );
                throw new Error(
                    'User identity not available for record creation.'
                );
            }

            let processedCount = 0;
            for (const item of batch) {
                try {
                    await dataProvider.create('endorsement_definitions', {
                        data: {
                            endorsement: item.endorsement,
                            endorsement_alias: item.endorsement_alias,
                            endorsement_type: item.endorsement_type,
                            definition: item.definition,
                            tags: item.tags,
                            created_at: new Date().toISOString(),
                            spreadsheet_type: selectedSpreadsheet,
                        },
                        meta: { user: identity },
                    });
                    processedCount++;
                } catch (error) {
                    console.error(
                        'useEndorsementDefsImport: Failed to create endorsement definition:',
                        item,
                        'Error:',
                        error
                    );
                    throw error;
                }
            }
            console.log(
                `useEndorsementDefsImport: Successfully processed ${processedCount} records.`
            );
        },
        [dataProvider, selectedSpreadsheet, identity]
    );

    return processBatch;
}
